import { useState } from 'react';
import {
  DAILY_GOAL,
  MISTAKE_KINDS,
  MISTAKE_LABELS,
  MISTAKE_REASONS,
  NOTATION_DRILLS,
  REASON_INFO,
  SCHAAK_THEMES,
  THEME_MASTERY,
  WOODPECKER_CYCLES,
  currentCycle,
  cycleDaysLeft,
  dueCards,
  focusOf,
  formatEval,
  isLearned,
  localDay,
  streakDays,
  themeOf,
  themeSolves,
  unlockedThemes,
  type SchaakImportResponse,
  type WoodpeckerCycle,
} from '@familyhub/shared';
import { Sheet } from '../platform/Sheet';
import { api, isOffline } from '../api/client';
import type { SchaakState } from './actions';
import { RESULT, freshMistakes, gameLink, plural, shortDate, todayCount } from './common';
import type { GameRecord } from './db';
import { notationMastered } from './Notation';
import { WOODPECKER_SET_NO, type Pack } from './pack';
import { schaakSync } from './sync';

/**
 * The Schaak overview (docs/SCHAAK.md §3.7): what to do today first, then the
 * one thing to work on, his games, notation, Woodpecker and the theme ladder.
 * Ordered by what the research says matters — the daily habit and his own
 * mistakes on top, the tools below.
 */

export interface OverviewActions {
  onTrain: (themes?: string[]) => void;
  onWoodpecker: () => void;
  onTheme: (key: string) => void;
  onMistakes: (gameId?: string) => void;
  onAccounts: () => void;
  onNotation: () => void;
}

export function Overview({ state, pack, actions }: { state: SchaakState; pack: Pack; actions: OverviewActions }) {
  const now = new Date();
  return (
    <main className="sk-main">
      <Hero state={state} now={now} onTrain={() => actions.onTrain()} />
      <FocusCard state={state} now={now} onTrain={(t) => actions.onTrain([t])} />
      <GamesCard state={state} onMistakes={actions.onMistakes} onAccounts={actions.onAccounts} />
      <NotationCard state={state} onOpen={actions.onNotation} />
      <WoodpeckerCard state={state} pack={pack} now={now} onOpen={actions.onWoodpecker} />
      <ThemesCard state={state} onTheme={actions.onTheme} />
      <p className="sk-advice">
        Speel daarnaast rustige partijen — 15+10 op Lichess. Je fouten komen hier vanzelf terug als puzzels.
      </p>
    </main>
  );
}

/* ---------------- today ---------------- */

/** Rating over the last 30 logged days, as a small line. */
function Sparkline({ points }: { points: number[] }) {
  if (points.length < 2) return null;
  const min = Math.min(...points);
  const max = Math.max(...points);
  const span = Math.max(20, max - min);
  const w = 100;
  const h = 28;
  const xy = points.map((p, i) => [(i / (points.length - 1)) * w, h - 3 - ((p - min) / span) * (h - 6)] as const);
  const d = xy.map(([x, y], i) => `${i ? 'L' : 'M'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  const [lx, ly] = xy[xy.length - 1]!;
  return (
    <svg className="sk-spark" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" aria-hidden>
      <path d={`${d} L${w},${h} L0,${h} Z`} className="sk-spark-fill" />
      <path d={d} className="sk-spark-line" vectorEffect="non-scaling-stroke" />
      <circle cx={lx} cy={ly} r="2.2" className="sk-spark-dot" />
    </svg>
  );
}

function FlameIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M12 2c.6 3.2-1.1 5-2.6 6.7C7.9 10.4 6.5 12 6.5 14.8A5.5 5.5 0 0 0 12 20.5a5.5 5.5 0 0 0 5.5-5.7c0-2.3-1.1-3.9-2.2-5.1-.2 1.4-.9 2.5-2 3 .5-3.6-.5-7.6-1.3-10.7Z" />
    </svg>
  );
}

function Hero({ state, now, onTrain }: { state: SchaakState; now: Date; onTrain: () => void }) {
  const today = todayCount(state, now);
  const streak = streakDays(state.days, localDay(now));
  const playedToday = state.days.some((d) => d.d === localDay(now) && d.n > 0);
  const recent = state.days.slice(-30);
  const weekAgo = new Date(now.getTime() - 7 * 86_400_000);
  const base = [...state.days].reverse().find((d) => d.d <= localDay(weekAgo)) ?? state.days[0];
  const week = base ? state.rating - base.r : 0;
  const due = dueCards(state.cards, now).filter((c) => c.kind !== 'drill');
  const dueMistakes = due.filter((c) => c.kind === 'mistake').length;
  const fresh = freshMistakes(state).length;
  const plan = [
    due.length - dueMistakes > 0 && plural(due.length - dueMistakes, 'herhaling', 'herhalingen'),
    dueMistakes + fresh > 0 && plural(dueMistakes + fresh, 'eigen fout', 'eigen fouten'),
    'nieuwe puzzels',
  ].filter(Boolean) as string[];
  const pct = Math.min(100, (100 * today) / DAILY_GOAL);

  return (
    <section className="sk-hero">
      <div className="sk-hero-top">
        <div className="sk-rating">
          <span className="sk-rating-value">{state.rating}</span>
          <span className="sk-stat-label">
            puzzelrating
            {week !== 0 && (
              <span className={week > 0 ? 'sk-up' : 'sk-down'}>
                {' '}
                · {week > 0 ? '+' : '−'}
                {Math.abs(week)} deze week
              </span>
            )}
          </span>
        </div>
        <div className={`sk-streak${playedToday ? ' is-lit' : ''}`} aria-label={`${streak} dagen op rij`}>
          <FlameIcon />
          <span>
            {streak} <span className="sk-streak-unit">{streak === 1 ? 'dag' : 'dagen'}</span>
          </span>
        </div>
      </div>
      <Sparkline points={recent.map((d) => d.r)} />
      <div className="sk-goal">
        <div className="sk-bar" aria-hidden>
          <span style={{ width: `${pct}%` }} />
        </div>
        <span className="sk-goal-text">
          {today >= DAILY_GOAL ? `Dagdoel gehaald · ${today}` : `${today} van ${DAILY_GOAL} vandaag`}
        </span>
      </div>
      <button className="btn btn-primary sk-start" onClick={onTrain}>
        Trainen
      </button>
      <p className="sk-plan">{plan.join(' · ')}</p>
    </section>
  );
}

/* ---------------- the one thing to work on ---------------- */

function FocusCard({ state, now, onTrain }: { state: SchaakState; now: Date; onTrain: (theme: string) => void }) {
  const playedAt = new Map(state.games.map((g) => [g.id, g.played_at]));
  const facts = state.mistakes.map((m) => ({
    kind: m.kind,
    played_at: playedAt.get(m.game_id) ?? m.created_at,
    reason: state.cards[m.id]?.reason ?? null,
  }));
  const focus = focusOf(facts, now);
  if (!focus) return null;
  const label = MISTAKE_LABELS[focus.kind]!;
  const theme = themeOf(focus.theme);
  const reasons = MISTAKE_REASONS.filter((r) => r !== 'unsure')
    .map((r) => ({ r, n: facts.filter((f) => f.reason === r).length }))
    .filter((x) => x.n > 0)
    .sort((a, b) => b.n - a.n);

  return (
    <section className="sk-card sk-focus" aria-label="Jouw aandachtspunt">
      <p className="sk-eyebrow">Jouw aandachtspunt</p>
      <h2>
        {label.label} <span className="sk-count">{focus.count}× deze maand</span>
      </h2>
      <p className="sk-tip">{focus.advice}</p>
      {reasons.length > 0 && (
        <p className="sk-dim sk-small">
          Jij zei: {reasons.map(({ r, n }) => `${REASON_INFO[r].label.toLowerCase()} (${n}×)`).join(', ')}.
        </p>
      )}
      {theme && (
        <button className="btn" onClick={() => onTrain(focus.theme)}>
          Oefen {theme.label.toLowerCase()}
        </button>
      )}
    </section>
  );
}

/* ---------------- his games ---------------- */

function GamesCard({
  state,
  onMistakes,
  onAccounts,
}: {
  state: SchaakState;
  onMistakes: (gameId?: string) => void;
  onAccounts: () => void;
}) {
  const [status, setStatus] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState<GameRecord | null>(null);
  const linked = !!(state.accounts.lichess || state.accounts.chesscom);
  const now = new Date();

  if (!linked) {
    return (
      <section className="sk-card">
        <h2>Jouw partijen</h2>
        <p className="sk-dim">
          Koppel je Lichess- en Chess.com-account. Dan zoekt de computer je fouten op en worden ze puzzels.
        </p>
        <button className="btn" onClick={onAccounts}>
          Account koppelen
        </button>
      </section>
    );
  }

  const fresh = freshMistakes(state).length;
  const due = dueCards(state.cards, now).filter((c) => c.kind === 'mistake').length;
  const learned = state.mistakes.filter((m) => isLearned(state.cards[m.id])).length;
  const pending = state.games.filter((g) => !g.analysed_at).length;
  const month = new Date(now.getTime() - 30 * 86_400_000).toISOString();
  const gameDate = new Map(state.games.map((g) => [g.id, g.played_at]));
  const recent = state.mistakes.filter((m) => (gameDate.get(m.game_id) ?? '') >= month);
  const byKind = MISTAKE_KINDS.map((k) => ({ k, n: recent.filter((m) => m.kind === k).length })).filter((x) => x.n > 0);
  const maxKind = Math.max(1, ...byKind.map((x) => x.n));

  const fetchNow = async () => {
    setBusy(true);
    setStatus(null);
    try {
      await schaakSync.sync(); // the accounts row must be on the server first
      const r = await api<SchaakImportResponse>('/api/v1/schaak/import', { method: 'POST' });
      const failed = r.failed.map((f) => (f.source === 'lichess' ? 'Lichess' : 'Chess.com'));
      setStatus(
        [
          r.imported === 0 ? 'Geen nieuwe partijen.' : `${plural(r.imported, 'nieuwe partij', 'nieuwe partijen')}.`,
          r.pending_analysis > 0 ? 'De computer analyseert ze binnen een kwartier.' : '',
          failed.length ? `${failed.join(' en ')} gaf geen antwoord — klopt de gebruikersnaam?` : '',
        ]
          .filter(Boolean)
          .join(' '),
      );
      void schaakSync.sync();
    } catch (err) {
      setStatus(isOffline(err) ? 'Geen verbinding — probeer het straks nog eens.' : 'Ophalen lukte niet.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="sk-card">
      <h2>Jouw partijen</h2>
      {state.games.length === 0 ? (
        <p className="sk-dim">Nog geen partijen opgehaald. Dat gebeurt elke ochtend, of nu met de knop.</p>
      ) : (
        <div className="sk-tiles">
          <div>
            <span className="sk-tile-value">{state.games.length}</span>
            <span className="sk-stat-label">partijen</span>
          </div>
          <div>
            <span className="sk-tile-value">{state.mistakes.length}</span>
            <span className="sk-stat-label">fouten</span>
          </div>
          <div>
            <span className="sk-tile-value sk-up">{learned}</span>
            <span className="sk-stat-label">geleerd</span>
          </div>
        </div>
      )}
      {pending > 0 && <p className="sk-dim sk-small">{plural(pending, 'partij', 'partijen')} nog te analyseren…</p>}
      {byKind.length > 0 && (
        <div className="sk-kinds" aria-label="Fouten afgelopen maand">
          <p className="sk-dim sk-small">Afgelopen maand</p>
          {byKind.map(({ k, n }) => (
            <div key={k} className="sk-kind">
              <span>{MISTAKE_LABELS[k]!.label}</span>
              <span className="sk-kind-bar" aria-hidden>
                <span style={{ width: `${(100 * n) / maxKind}%` }} />
              </span>
              <span className="sk-kind-n">{n}</span>
            </div>
          ))}
        </div>
      )}
      {fresh + due > 0 && (
        <button className="btn btn-accent" onClick={() => onMistakes()}>
          Oefen je fouten · {fresh + due}
        </button>
      )}
      {state.games.length > 0 && (
        <ul className="sk-games" aria-label="Laatste partijen">
          {state.games.slice(0, 6).map((g) => {
            const n = state.mistakes.filter((m) => m.game_id === g.id).length;
            return (
              <li key={g.id}>
                <button className="sk-game" onClick={() => setOpen(g)} aria-label={`Partij tegen ${g.opponent}`}>
                  <span className={`sk-result is-${g.result}`} aria-label={RESULT[g.result]!.label}>
                    {RESULT[g.result]!.short}
                  </span>
                  <span className="sk-game-who">
                    {g.opponent}
                    {g.opponent_rating ? <span className="sk-dim"> {g.opponent_rating}</span> : null}
                  </span>
                  <span className="sk-game-meta">
                    {g.analysed_at ? plural(n, 'fout', 'fouten') : '…'} · {shortDate(g.played_at)}
                  </span>
                  <span className="sk-chev" aria-hidden>
                    ›
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
      <button className="btn btn-quiet" onClick={() => void fetchNow()} disabled={busy}>
        {busy ? 'Bezig…' : 'Nu ophalen'}
      </button>
      {status && (
        <p className="sk-dim" role="status">
          {status}
        </p>
      )}
      {open && (
        <GameSheet
          game={open}
          state={state}
          onPractise={() => {
            setOpen(null);
            onMistakes(open.id);
          }}
          onClose={() => setOpen(null)}
        />
      )}
    </section>
  );
}

function GameSheet({
  game,
  state,
  onPractise,
  onClose,
}: {
  game: GameRecord;
  state: SchaakState;
  onPractise: () => void;
  onClose: () => void;
}) {
  const mistakes = state.mistakes.filter((m) => m.game_id === game.id).sort((a, b) => a.ply - b.ply);
  const moveNo = (ply: number) => `${Math.floor(ply / 2) + 1}${ply % 2 === 0 ? '.' : '...'}`;
  return (
    <Sheet title={`Tegen ${game.opponent}`} onClose={onClose}>
      <p className="sk-dim">
        {RESULT[game.result]!.label} · {game.speed} · {shortDate(game.played_at)}
        {game.opening ? ` · ${game.opening}` : ''}
      </p>
      {!game.analysed_at ? (
        <p className="sk-dim">Nog niet geanalyseerd.</p>
      ) : mistakes.length === 0 ? (
        <p className="sk-dim">Geen grote fouten gevonden in deze partij. Netjes.</p>
      ) : (
        <ul className="sk-mlist">
          {mistakes.map((m) => {
            const card = state.cards[m.id];
            const drop =
              m.eval_before_cp != null || m.eval_before_mate != null
                ? `${formatEval({ cp: m.eval_before_cp ?? null, mate: m.eval_before_mate ?? null })} → ${formatEval({
                    cp: m.eval_after_cp ?? null,
                    mate: m.eval_after_mate ?? null,
                  })}`
                : null;
            return (
              <li key={m.id}>
                <span className="sk-mlist-move">
                  {moveNo(m.ply)} {m.played_san}
                </span>
                <span className="sk-mlist-what">
                  {MISTAKE_LABELS[m.kind]!.label}
                  {drop && <span className="sk-dim"> · {drop}</span>}
                </span>
                <span className={`sk-badge${isLearned(card) ? ' is-learned' : card ? '' : ' is-new'}`}>
                  {isLearned(card) ? 'geleerd' : card ? 'bezig' : 'nieuw'}
                </span>
              </li>
            );
          })}
        </ul>
      )}
      {mistakes.length > 0 && (
        <button className="btn btn-primary" onClick={onPractise}>
          Oefen deze partij
        </button>
      )}
      <a className="btn btn-quiet" href={gameLink(game)} target="_blank" rel="noreferrer">
        Open op {game.source === 'lichess' ? 'Lichess' : 'Chess.com'} ↗
      </a>
    </Sheet>
  );
}

/* ---------------- the tools ---------------- */

function NotationCard({ state, onOpen }: { state: SchaakState; onOpen: () => void }) {
  const n = notationMastered(state);
  return (
    <section className="sk-card sk-row-card">
      <div>
        <h2>Notatie</h2>
        <p className="sk-dim sk-small">
          Zetten lezen: <strong>e4</strong>, <strong>Nxf7+</strong>, <strong>O-O</strong> · {n}/{NOTATION_DRILLS.length}{' '}
          beheerst
        </p>
      </div>
      <button className="btn" onClick={onOpen}>
        Leren
      </button>
    </section>
  );
}

function daysLeftText(d: number): string {
  if (d < 0) return `${-d} dag${d === -1 ? '' : 'en'} over het doel`;
  if (d === 0) return 'doel: vandaag af';
  return `doel: nog ${d} dag${d === 1 ? '' : 'en'}`;
}

const minutes = (s: number) => (Math.round(s / 60) < 1 ? '<1 min' : `${Math.round(s / 60)} min`);

function CycleHistory({ cycles }: { cycles: readonly WoodpeckerCycle[] }) {
  const done = cycles.filter((c) => c.set_no === WOODPECKER_SET_NO && c.finished_at).sort((a, b) => a.cycle - b.cycle);
  if (done.length === 0) return null;
  return (
    <ol className="sk-cycles" aria-label="Gedane cycli">
      {done.map((c) => (
        <li key={c.cycle}>
          Cyclus {c.cycle}: {Math.round((100 * c.correct) / Math.max(1, c.position))}% goed · {minutes(c.seconds)}
        </li>
      ))}
    </ol>
  );
}

function WoodpeckerCard({ state, pack, now, onOpen }: { state: SchaakState; pack: Pack; now: Date; onOpen: () => void }) {
  const cycle = currentCycle(state.cycles, WOODPECKER_SET_NO, now);
  const size = pack.woodpecker.length;
  return (
    <section className="sk-card">
      <div className="sk-row-card">
        <div>
          <h2>Woodpecker</h2>
          <p className="sk-dim sk-small">
            {cycle
              ? `Dezelfde ${size} puzzels, steeds sneller · cyclus ${cycle.cycle}/${WOODPECKER_CYCLES} · ${cycle.position}/${size}`
              : `Alle ${WOODPECKER_CYCLES} cycli gedaan. Deze patronen zitten erin.`}
            {cycle && cycle.position > 0 && <> · {daysLeftText(cycleDaysLeft(cycle, now))}</>}
          </p>
        </div>
        {cycle && (
          <button className="btn" onClick={onOpen}>
            {cycle.position === 0 ? 'Begin' : 'Verder'}
          </button>
        )}
      </div>
      {cycle && cycle.position > 0 && (
        <div className="sk-bar" aria-hidden>
          <span style={{ width: `${(100 * cycle.position) / size}%` }} />
        </div>
      )}
      <CycleHistory cycles={state.cycles} />
    </section>
  );
}

function ThemesCard({ state, onTheme }: { state: SchaakState; onTheme: (key: string) => void }) {
  const unlocked = new Set(unlockedThemes(state.cards));
  return (
    <section className="sk-card">
      <h2>Thema's</h2>
      <ul className="sk-theme-grid">
        {SCHAAK_THEMES.map((t) => {
          const open = unlocked.has(t.key);
          const n = themeSolves(state.cards, t.key);
          const done = n >= THEME_MASTERY;
          return (
            <li key={t.key}>
              <button
                className={`sk-theme-tile${done ? ' is-done' : ''}${open ? '' : ' is-locked'}`}
                onClick={() => onTheme(t.key)}
                aria-label={`${t.label}: ${!open ? 'op slot' : done ? 'beheerst' : `${n} van ${THEME_MASTERY}`}`}
              >
                <span className="sk-theme-label">{t.label}</span>
                <span className="sk-ring" aria-hidden>
                  <span style={{ width: `${Math.min(100, (100 * n) / THEME_MASTERY)}%` }} />
                </span>
                <span className="sk-theme-state">{!open ? 'op slot' : done ? '✓ beheerst' : `${n}/${THEME_MASTERY}`}</span>
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

export function ThemeSheet({
  theme,
  solves,
  unlocked,
  onStart,
  onClose,
}: {
  theme: string;
  solves: number;
  unlocked: boolean;
  onStart: () => void;
  onClose: () => void;
}) {
  const t = themeOf(theme)!;
  return (
    <Sheet title={t.label} onClose={onClose}>
      <p>{t.intro}</p>
      <p className="sk-look">
        <strong>Vraag jezelf:</strong> {t.look}
      </p>
      <p className="sk-dim">
        {solves >= THEME_MASTERY
          ? `Beheerst — ${solves} in één keer goed.`
          : unlocked
            ? `${solves} van ${THEME_MASTERY} in één keer goed; daarna gaat het volgende thema open.`
            : 'Dit thema gaat open als het vorige beheerst is. Je mag het nu al proberen.'}
      </p>
      <button className="btn btn-primary" onClick={onStart}>
        Oefen dit thema
      </button>
    </Sheet>
  );
}

const USERNAME = /^[A-Za-z0-9_-]{2,30}$/;

export function AccountsSheet({
  accounts,
  onSave,
  onClose,
}: {
  accounts: SchaakState['accounts'];
  onSave: (lichess: string | null, chesscom: string | null) => void;
  onClose: () => void;
}) {
  const [lichess, setLichess] = useState(accounts.lichess ?? '');
  const [chesscom, setChesscom] = useState(accounts.chesscom ?? '');
  const clean = (v: string) => v.trim() || null;
  const ok = [lichess, chesscom].every((v) => !v.trim() || USERNAME.test(v.trim()));
  return (
    <Sheet title="Accounts" onClose={onClose}>
      <p className="sk-dim">Je partijen worden elke ochtend opgehaald. Alleen openbare partijen; geen wachtwoord nodig.</p>
      <label className="sk-field">
        Lichess-gebruikersnaam
        <input
          className="input"
          value={lichess}
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          onChange={(e) => setLichess(e.target.value)}
        />
      </label>
      <label className="sk-field">
        Chess.com-gebruikersnaam
        <input
          className="input"
          value={chesscom}
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          onChange={(e) => setChesscom(e.target.value)}
        />
      </label>
      {!ok && <p className="sk-bad">Een gebruikersnaam is 2–30 letters, cijfers, - of _.</p>}
      <button
        className="btn btn-primary"
        disabled={!ok}
        onClick={() => {
          onSave(clean(lichess), clean(chesscom));
          onClose();
        }}
      >
        Bewaren
      </button>
    </Sheet>
  );
}
